import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  Card, 
  CardHeader,
  CardBody, 
  Button, 
  OutlineButton, 
  Badge, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableHeader,
  FlexContainer,
  Input,
  Select,
  Pagination,
  PageButton
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { AccountingEntry, AccountingType } from '../../types';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import apiService from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatUtils';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const FilterSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  background-color: white;
  padding: 1rem;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  label {
    font-weight: 500;
    color: var(--dark-gray);
    white-space: nowrap;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 300px;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const AmountText = styled.span<{ type: AccountingType }>`
  font-weight: 500;
  color: ${props => props.type === 'Income' ? 'var(--success-color)' : 
    props.type === 'Expense' ? 'var(--danger-color)' : 'var(--text-color)'};
`;

const DateText = styled.span`
  color: var(--dark-gray);
  font-size: 0.85rem;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--dark-gray);
`;

const Summary = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SummaryCard = styled.div<{ type?: string }>`
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  padding: 1.5rem;
  text-align: center;
  
  h3 {
    margin: 0 0 0.5rem 0;
    color: var(--secondary-color);
    font-size: 1rem;
  }
  
  .amount {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${props => props.type === 'income' ? 'var(--success-color)' : 
      props.type === 'expense' ? 'var(--danger-color)' : 'var(--primary-color)'};
  }
  
  .label {
    font-size: 0.85rem;
    color: var(--dark-gray);
    margin-top: 0.5rem;
  }
`;

const AnimatedModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;
const AnimatedModalContent = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(20,19,64,0.18);
  padding: 2.5rem 2rem 2rem 2rem;
  min-width: 350px;
  max-width: 95vw;
  text-align: center;
  animation: popIn 0.35s cubic-bezier(0.4,0,0.2,1);
  @keyframes popIn {
    0% { transform: scale(0.85); opacity: 0; }
    80% { transform: scale(1.05); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }
`;
const ModalTitle = styled.h2`
  color: #F68B21;
  margin-bottom: 1rem;
`;
const ModalMessage = styled.p`
  color: #141340;
  font-size: 1.1rem;
  margin-bottom: 2rem;
`;
const ModalActions = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
`;

const FinanceList: React.FC = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState<AccountingEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<AccountingEntry[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<string>('');
  const [modal, setModal] = useState<{ type: 'approve' | 'reject' | null, entryId?: string }>({ type: null });
  
  const itemsPerPage = 10;

  useEffect(() => {
    const loadFinanceData = async () => {
      try {
        // Load accounting entries from API
        const response = await apiService.getAccountingEntries({ limit: 1000 });
        const allEntries = response.data || [];
        
        // Filter only office-related entries (those without projectId)
        const officeEntries = allEntries.filter((entry: any) => !entry.projectId);
        setEntries(officeEntries);
        setFilteredEntries(officeEntries);
      } catch (error) {
        console.error('Error loading finance data:', error);
        // Fallback to empty array
        setEntries([]);
        setFilteredEntries([]);
      }
    };

    loadFinanceData();
  }, []);

  useEffect(() => {
    let result = [...entries];
    
    // Apply search filter
    if (searchTerm) {
      result = result.filter(
        entry => 
          entry.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          entry.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
          entry.account.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply type filter
    if (typeFilter) {
      result = result.filter(entry => entry.type === typeFilter);
    }
    
    // Apply category filter
    if (categoryFilter) {
      result = result.filter(entry => entry.category === categoryFilter);
    }
    
    // Apply date filter
    if (dateFilter) {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
      
      switch (dateFilter) {
        case 'today':
          result = result.filter(entry => {
            const entryDate = new Date(entry.date);
            return entryDate.toDateString() === today.toDateString();
          });
          break;
        case 'thisMonth':
          result = result.filter(entry => {
            const entryDate = new Date(entry.date);
            return entryDate >= firstDayOfMonth;
          });
          break;
        case 'thisYear':
          result = result.filter(entry => {
            const entryDate = new Date(entry.date);
            return entryDate >= firstDayOfYear;
          });
          break;
        default:
          break;
      }
    }
    
    // Default sort by date (newest first)
    result.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    
    setFilteredEntries(result);
  }, [entries, searchTerm, typeFilter, categoryFilter, dateFilter]);

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredEntries.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEntries.length / itemsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  // Calculate summary statistics
  const totalIncome = entries
    .filter(entry => entry.type === 'Income')
    .reduce((sum, entry) => sum + entry.amount, 0);
  
  const totalExpense = entries
    .filter(entry => entry.type === 'Expense')
    .reduce((sum, entry) => sum + entry.amount, 0);
  
  // Use real company balance if available, otherwise calculate from entries
  const [companyBalance, setCompanyBalance] = useState<any>(null);
  
  useEffect(() => {
    const loadCompanyBalance = async () => {
      try {
        const balanceResponse = await apiService.getCompanyBalance();
        setCompanyBalance(balanceResponse.data);
      } catch (error) {
        console.error('Error loading company balance:', error);
      }
    };
    
    loadCompanyBalance();
  }, []);
  
  // Company Balance should be Total Income - Total Expenses
  const netBalance = totalIncome - totalExpense;

  // Get unique categories for filter
  const uniqueCategories = Array.from(new Set(entries.map(entry => entry.category)));

  // Use the imported formatCurrency and formatDate functions

  // Check if current user can manage finances
  const canManageFinances = user?.role === 'MD' || user?.role === 'TD' || user?.role === 'Accountant';

  return (
    <div>
      <PageHeader>
        <h1 className="page-title">Office Finance</h1>
        {canManageFinances && (
          <ActionButtons>
            <Button as={Link} to="/office/finance/new">
              + New Entry
            </Button>
            <OutlineButton as={Link} to="/office/budget">
              Budget
            </OutlineButton>

            <OutlineButton as={Link} to="/office/summary">
              Financial Summary
            </OutlineButton>
            <OutlineButton as={Link} to="/office/loans">
              Loans
            </OutlineButton>
            <OutlineButton>
              Excel
            </OutlineButton>
            <OutlineButton>
              PDF
            </OutlineButton>
          </ActionButtons>
        )}
      </PageHeader>

      <Summary>
        <SummaryCard type="income">
          <h3>Total Income</h3>
          <div className="amount">{formatCurrency(totalIncome, 'TZS')}</div>
          <div className="label">All time</div>
        </SummaryCard>
        
        <SummaryCard type="expense">
          <h3>Total Expenses</h3>
          <div className="amount">{formatCurrency(totalExpense, 'TZS')}</div>
          <div className="label">All time</div>
        </SummaryCard>
        
        <SummaryCard type={netBalance >= 0 ? 'income' : 'expense'}>
          <h3>Company Balance</h3>
          <div className="amount">{formatCurrency(netBalance, 'TZS')}</div>
          <div className="label">Current available balance</div>
        </SummaryCard>
        
        <SummaryCard type="budget">
          <h3>Remaining Budget</h3>
          <div className="amount" style={{ 
            color: netBalance >= 0 ? 'var(--success-color)' : 'var(--danger-color)' 
          }}>
            {formatCurrency(netBalance, 'TZS')}
          </div>
          <div className="label">Total Income - Total Expenses</div>
        </SummaryCard>
      </Summary>

      <FilterSection>
        <SearchContainer>
          <Input 
            type="text" 
            placeholder="Search entries..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>

        <FilterGroup>
          <label>Type:</label>
          <Select 
            value={typeFilter} 
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All Types</option>
            <option value="Income">Income</option>
            <option value="Expense">Expense</option>
            <option value="Asset">Asset</option>
            <option value="Liability">Liability</option>
            <option value="Equity">Equity</option>
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>Category:</label>
          <Select 
            value={categoryFilter} 
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">All Categories</option>
            {uniqueCategories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>Date:</label>
          <Select 
            value={dateFilter} 
            onChange={(e) => setDateFilter(e.target.value)}
          >
            <option value="">All Time</option>
            <option value="today">Today</option>
            <option value="thisMonth">This Month</option>
            <option value="thisYear">This Year</option>
          </Select>
        </FilterGroup>

        <Button onClick={() => {
          setSearchTerm('');
          setTypeFilter('');
          setCategoryFilter('');
          setDateFilter('');
        }}>
          Reset Filters
        </Button>
      </FilterSection>

      <Card>
        <CardHeader>
          <h3>Financial Entries ({filteredEntries.length})</h3>
        </CardHeader>
        <CardBody>
          {filteredEntries.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>ID</TableHeader>
                    <TableHeader>Description</TableHeader>
                    <TableHeader>Amount</TableHeader>
                    <TableHeader>Type</TableHeader>
                    <TableHeader>Category</TableHeader>
                    <TableHeader>Account</TableHeader>
                    <TableHeader>Date</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </TableRow>
                </TableHead>
                <tbody>
                  {currentItems.map((entry) => (
                    <TableRow key={entry._id || entry.id}>
                      <TableCell>
                        <Link to={`/office/finance/${entry._id || entry.id}`}>{entry._id || entry.id}</Link>
                      </TableCell>
                      <TableCell>{entry.description}</TableCell>
                      <TableCell>
                        <AmountText type={entry.type}>
                          {formatCurrency(entry.amount, entry.currency)}
                        </AmountText>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={
                            entry.type === 'Income' ? 'success' : 
                            entry.type === 'Expense' ? 'danger' : 
                            entry.type === 'Asset' ? 'info' : 
                            entry.type === 'Liability' ? 'warning' : 'secondary'
                          }
                        >
                          {entry.type}
                        </Badge>
                      </TableCell>
                      <TableCell>{entry.category}</TableCell>
                      <TableCell>{entry.account}</TableCell>
                      <TableCell>
                        <DateText>{formatDate(entry.date)}</DateText>
                      </TableCell>
                      <TableCell>
                        <FlexContainer gap="0.5rem">
                          <OutlineButton as={Link} to={`/office/finance/${entry._id || entry.id}`} title="View Details">
                            View
                          </OutlineButton>
                          
                          {canManageFinances && (
                            <OutlineButton as={Link} to={`/office/finance/edit/${entry._id || entry.id}`} title="Edit Entry">
                              Edit
                            </OutlineButton>
                          )}
                          {entry.pendingEFDApproval && (
                            <span style={{ color: '#ffc107', fontWeight: 600, fontSize: '0.95rem' }}>Pending</span>
                          )}
                          {entry.pendingEFDApproval && (user?.role === 'MD' || user?.role === 'TD') && (
                            <>
                              <Button style={{ background: 'var(--success-color)', color: '#fff' }} onClick={() => setModal({ type: 'approve', entryId: entry._id || entry.id })}>Approve</Button>
                              <Button style={{ background: 'var(--danger-color)', color: '#fff' }} onClick={() => setModal({ type: 'reject', entryId: entry._id || entry.id })}>Reject</Button>
                            </>
                          )}
                        </FlexContainer>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          ) : (
            <NoDataMessage>
              <p>No financial entries found matching your filters.</p>
            </NoDataMessage>
          )}

          {totalPages > 1 && (
            <Pagination>
              <PageButton 
                onClick={() => paginate(currentPage - 1)} 
                disabled={currentPage === 1}
              >
                &laquo;
              </PageButton>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                <PageButton
                  key={number}
                  active={number === currentPage}
                  onClick={() => paginate(number)}
                >
                  {number}
                </PageButton>
              ))}
              
              <PageButton 
                onClick={() => paginate(currentPage + 1)} 
                disabled={currentPage === totalPages}
              >
                &raquo;
              </PageButton>
            </Pagination>
          )}
        </CardBody>
      </Card>

      {modal.type && (
        <AnimatedModalOverlay onClick={() => setModal({ type: null })}>
          <AnimatedModalContent onClick={e => e.stopPropagation()}>
            {modal.type === 'approve'
              ? FaCheckCircle({ size: 48, color: '#28a745', style: { marginBottom: 16 } })
              : FaTimesCircle({ size: 48, color: '#dc3545', style: { marginBottom: 16 } })}
            <ModalTitle>{modal.type === 'approve' ? 'Expense Approved' : 'Expense Rejected'}</ModalTitle>
            <ModalMessage>
              {modal.type === 'approve'
                ? 'The expense has been approved (dummy mode).'
                : 'The expense has been rejected (dummy mode).'}
            </ModalMessage>
            <ModalActions>
              <Button onClick={() => setModal({ type: null })} style={{ minWidth: 120, fontWeight: 600 }}>OK</Button>
            </ModalActions>
          </AnimatedModalContent>
        </AnimatedModalOverlay>
      )}

      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
    </div>
  );
};

export default FinanceList; 